import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, PencilSimple, ShieldCheck } from '@phosphor-icons/react';
import api, { formatApiError } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';

const LIMIT_FIELDS = [
  'max_discovery_jobs',
  'max_companies',
  'max_contacts',
  'max_exports',
  'max_export_records',
  'max_contact_reveals',
  'max_seats',
  'max_pushed_leads',
];

const FEATURE_FIELDS = [
  'has_pro_email_integration',
  'has_bulk_verification',
  'has_personal_contact_discovery',
  'has_custom_scoring',
  'has_api_access',
  'has_priority_support',
];

const EMPTY_PRODUCT = {
  name: '',
  display_name: '',
  description: '',
  price_monthly: 49,
  price_yearly: 470,
  is_active: true,
  is_public: true,
  is_trial: false,
  trial_days: 0,
  sort_order: 10,
  limits: {
    max_discovery_jobs: 10,
    max_companies: 500,
    max_contacts: 2000,
    max_exports: 5,
    max_export_records: 2000,
    max_contact_reveals: 250,
    max_seats: 1,
    max_pushed_leads: 100,
  },
  features: Object.fromEntries(FEATURE_FIELDS.map((field) => [field, false])),
  reason: '',
  updated_at: null,
};

function copyProduct(product = EMPTY_PRODUCT) {
  return {
    ...EMPTY_PRODUCT,
    ...product,
    limits: { ...EMPTY_PRODUCT.limits, ...(product.limits || {}) },
    features: { ...EMPTY_PRODUCT.features, ...(product.features || {}) },
    reason: '',
  };
}

function changedKeys(event) {
  const before = event.before || {};
  const after = event.after || {};
  if (!Object.keys(before).length) return [];
  const scalar = ['display_name', 'description', 'price_monthly', 'price_yearly', 'is_active', 'is_public', 'trial_days', 'sort_order'];
  const keys = scalar.filter((key) => JSON.stringify(before[key]) !== JSON.stringify(after[key]));
  if (JSON.stringify(before.limits || {}) !== JSON.stringify(after.limits || {})) keys.push('limits');
  if (JSON.stringify(before.features || {}) !== JSON.stringify(after.features || {})) keys.push('features');
  return keys;
}

export default function ProductsAdminPage() {
  const { t, i18n } = useTranslation('products');
  const [plans, setPlans] = useState([]);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(copyProduct());
  const [saving, setSaving] = useState(false);
  const locale = i18n.resolvedLanguage === 'de' ? 'de-DE' : i18n.resolvedLanguage === 'es' ? 'es-ES' : 'en-GB';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [planResponse, auditResponse] = await Promise.all([
        api.get('/super-admin/subscriptions/plans'),
        api.get('/super-admin/subscriptions/plans/audit'),
      ]);
      setPlans(planResponse.data.items || []);
      setAudit(auditResponse.data.items || []);
    } catch (error) {
      toast.error(formatApiError(error) || t('error'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setCreating(true);
    setEditing(null);
    setForm(copyProduct());
  };

  const openEdit = (plan) => {
    setCreating(false);
    setEditing(plan);
    setForm(copyProduct(plan));
  };

  const closeDialog = () => {
    if (saving) return;
    setEditing(null);
    setCreating(false);
  };

  const updateNumber = (field, value) => setForm((old) => ({ ...old, [field]: value === '' ? '' : Number(value) }));
  const updateLimit = (field, value) => setForm((old) => ({
    ...old,
    limits: { ...old.limits, [field]: value === '' ? '' : Number(value) },
  }));

  const save = async () => {
    if (form.reason.trim().length < 3 || !form.display_name.trim()) return;
    const numericValues = [form.price_monthly, form.price_yearly, form.trial_days, form.sort_order, ...Object.values(form.limits)];
    if (numericValues.some((value) => value === '' || !Number.isFinite(Number(value)))) return;
    setSaving(true);
    try {
      const payload = {
        display_name: form.display_name.trim(),
        description: form.description.trim(),
        price_monthly: Number(form.price_monthly),
        price_yearly: Number(form.price_yearly),
        is_active: Boolean(form.is_active),
        is_public: Boolean(form.is_public),
        is_trial: Boolean(form.is_trial),
        trial_days: Number(form.trial_days),
        sort_order: Number(form.sort_order),
        limits: Object.fromEntries(LIMIT_FIELDS.map((field) => [field, Number(form.limits[field])])),
        features: Object.fromEntries(FEATURE_FIELDS.map((field) => [field, Boolean(form.features[field])])),
        reason: form.reason.trim(),
        expected_updated_at: creating ? null : form.updated_at,
      };
      if (creating) {
        await api.post('/super-admin/subscriptions/plans', { ...payload, name: form.name.trim().toLowerCase() });
        toast.success(t('created'));
      } else {
        await api.put(`/super-admin/subscriptions/plans/${editing.name}`, payload);
        toast.success(t('saved'));
      }
      setEditing(null);
      setCreating(false);
      await load();
    } catch (error) {
      toast.error(formatApiError(error) || t('error'));
    } finally {
      setSaving(false);
    }
  };

  const dialogOpen = Boolean(editing) || creating;

  return (
    <div className="mx-auto max-w-7xl space-y-6" data-testid="products-admin-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold">{t('title')}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t('subtitle')}</p>
        </div>
        <Button onClick={openCreate} data-testid="add-product"><Plus size={16} className="mr-2" />{t('add')}</Button>
      </div>

      <div className="overflow-x-auto rounded-lg border border-border bg-background">
        <table className="w-full min-w-[900px] text-sm">
          <thead className="border-b bg-muted/50 text-left text-xs uppercase text-muted-foreground">
            <tr><th className="p-3">{t('product')}</th><th className="p-3">{t('status')}</th><th className="p-3">{t('monthly')}</th><th className="p-3">{t('yearly')}</th><th className="p-3">{t('trial')}</th><th className="p-3">{t('customers')}</th><th className="p-3 text-right">{t('actions')}</th></tr>
          </thead>
          <tbody>
            {plans.map((plan) => (
              <tr key={plan.id} className="border-b last:border-0" data-testid={`product-row-${plan.name}`}>
                <td className="p-3"><div className="font-semibold">{plan.display_name}</div><div className="font-mono text-xs text-muted-foreground">{plan.name}</div></td>
                <td className="p-3"><div className="flex flex-wrap gap-1"><Status active={plan.is_active} labels={[t('active'), t('archived')]} /><Status active={plan.is_public} labels={[t('public'), t('hidden')]} /></div></td>
                <td className="p-3 font-medium">{plan.is_trial ? '—' : formatEuro(plan.price_monthly, locale)}</td>
                <td className="p-3 font-medium">{plan.is_trial ? '—' : formatEuro(plan.price_yearly, locale)}</td>
                <td className="p-3">{plan.is_trial ? t('days', { count: plan.trial_days }) : t('none')}</td>
                <td className="p-3">{Number(plan.subscription_count || 0).toLocaleString(locale)}</td>
                <td className="p-3 text-right"><Button size="sm" variant="outline" onClick={() => openEdit(plan)} data-testid={`edit-${plan.name}`}><PencilSimple size={14} className="mr-1" />{t('edit')}</Button></td>
              </tr>
            ))}
            {!loading && plans.length === 0 && <tr><td colSpan="7" className="p-8 text-center text-muted-foreground">{t('empty')}</td></tr>}
            {loading && <tr><td colSpan="7" className="p-8 text-center text-muted-foreground">{t('common:common.loading')}</td></tr>}
          </tbody>
        </table>
      </div>

      <section className="rounded-lg border border-border bg-background p-5" data-testid="product-audit">
        <div className="mb-4 flex items-center gap-2"><ShieldCheck size={20} /><h2 className="font-heading text-lg font-semibold">{t('auditTitle')}</h2></div>
        <div className="space-y-3">
          {audit.slice(0, 20).map((event) => {
            const fields = changedKeys(event).map((key) => t(`fields.${key}`)).join(', ');
            return <div key={event.id} className="rounded-md border p-3 text-sm"><div className="flex flex-wrap items-center justify-between gap-2"><strong>{event.plan_name} · {event.action === 'plan.create' ? t('auditCreate') : t('auditUpdate')}</strong><span className="text-xs text-muted-foreground">{event.created_at ? new Date(event.created_at).toLocaleString(locale) : ''}</span></div><p className="mt-1">{event.reason}</p><p className="mt-1 text-xs text-muted-foreground">{event.actor_email}{fields ? ` · ${t('changedFields', { fields })}` : ''}</p></div>;
          })}
          {!loading && audit.length === 0 && <p className="text-sm text-muted-foreground">{t('auditEmpty')}</p>}
        </div>
      </section>

      <Dialog open={dialogOpen} onOpenChange={(open) => !open && closeDialog()}>
        <DialogContent className="max-h-[92vh] max-w-4xl overflow-y-auto" data-testid="product-dialog">
          <DialogHeader><DialogTitle>{creating ? t('create') : `${t('edit')}: ${editing?.display_name}`}</DialogTitle><DialogDescription>{creating ? t('paid') : editing?.name}</DialogDescription></DialogHeader>

          <FormSection title={t('productDetails')}>
            <div className="grid gap-4 sm:grid-cols-2">
              {creating && <Field label={t('productKey')} hint={t('productKeyHint')}><Input value={form.name} onChange={(event) => setForm((old) => ({ ...old, name: event.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') }))} placeholder="enterprise" data-testid="product-key" /></Field>}
              <Field label={t('displayName')}><Input value={form.display_name} onChange={(event) => setForm((old) => ({ ...old, display_name: event.target.value }))} data-testid="product-display-name" /></Field>
              <Field label={t('sortOrder')}><Input type="number" min="0" value={form.sort_order} onChange={(event) => updateNumber('sort_order', event.target.value)} /></Field>
              <Field label={t('description')} className="sm:col-span-2"><textarea value={form.description} onChange={(event) => setForm((old) => ({ ...old, description: event.target.value }))} className="min-h-20 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" /></Field>
            </div>
          </FormSection>

          <FormSection title={form.is_trial ? t('trial') : t('pricing')}>
            {form.is_trial ? (
              <Field label={t('trialDays')}><Input type="number" min="1" max="3650" value={form.trial_days} onChange={(event) => updateNumber('trial_days', event.target.value)} data-testid="trial-days" /></Field>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2"><Field label={t('monthlyPrice')}><Input type="number" min="0" step="0.01" value={form.price_monthly} onChange={(event) => updateNumber('price_monthly', event.target.value)} data-testid="monthly-price" /></Field><Field label={t('yearlyPrice')}><Input type="number" min="0" step="0.01" value={form.price_yearly} onChange={(event) => updateNumber('price_yearly', event.target.value)} data-testid="yearly-price" /></Field></div>
            )}
          </FormSection>

          <FormSection title={t('availability')}>
            <div className="grid gap-3 sm:grid-cols-2">
              <Toggle checked={form.is_active} disabled={form.is_trial} onChange={(checked) => setForm((old) => ({ ...old, is_active: checked, is_public: checked ? old.is_public : false }))} label={t('activeLabel')} hint={t('activeHint')} />
              <Toggle checked={form.is_public} disabled={form.is_trial || !form.is_active} onChange={(checked) => setForm((old) => ({ ...old, is_public: checked }))} label={t('publicLabel')} hint={t('publicHint')} />
            </div>
          </FormSection>

          <FormSection title={t('limits')} hint={t('unlimitedHint')}>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">{LIMIT_FIELDS.map((field) => <Field key={field} label={t(`limitsLabels.${field}`)}><Input type="number" min="-1" max="100000000" value={form.limits[field]} onChange={(event) => updateLimit(field, event.target.value)} data-testid={`limit-${field}`} /></Field>)}</div>
          </FormSection>

          <FormSection title={t('features')}>
            <div className="grid gap-3 sm:grid-cols-2">{FEATURE_FIELDS.map((field) => <Toggle key={field} checked={Boolean(form.features[field])} onChange={(checked) => setForm((old) => ({ ...old, features: { ...old.features, [field]: checked } }))} label={t(`featureLabels.${field}`)} />)}</div>
          </FormSection>

          <div className="rounded-md border border-amber-300 bg-amber-50 p-4 text-sm text-amber-950 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-100">
            <strong>{t('impactTitle')}</strong><ul className="mt-2 list-disc space-y-1 pl-5"><li>{t('impactPrice')}</li><li>{t('impactAccess')}</li><li>{t('impactTrial')}</li><li>{t('impactArchive')}</li></ul>
          </div>
          <Field label={t('reason')}><Input value={form.reason} onChange={(event) => setForm((old) => ({ ...old, reason: event.target.value }))} placeholder={t('reasonPlaceholder')} data-testid="change-reason" /></Field>
          <DialogFooter><Button variant="outline" onClick={closeDialog} disabled={saving}>{t('common:common.cancel')}</Button><Button onClick={save} disabled={saving || form.reason.trim().length < 3 || !form.display_name.trim() || (creating && form.name.trim().length < 2)} data-testid="save-product">{saving ? t('saving') : t('save')}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function formatEuro(value, locale) { return new Intl.NumberFormat(locale, { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 }).format(Number(value || 0)); }
function Status({ active, labels }) { return <span className={`rounded-full px-2 py-1 text-xs ${active ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300'}`}>{active ? labels[0] : labels[1]}</span>; }
function FormSection({ title, hint, children }) { return <section className="space-y-3 border-t pt-4 first:border-t-0 first:pt-0"><div><h3 className="font-semibold">{title}</h3>{hint && <p className="text-xs text-muted-foreground">{hint}</p>}</div>{children}</section>; }
function Field({ label, hint, className = '', children }) { return <label className={`block ${className}`}><span className="mb-1 block text-xs font-medium text-muted-foreground">{label}</span>{children}{hint && <span className="mt-1 block text-xs text-muted-foreground">{hint}</span>}</label>; }
function Toggle({ checked, disabled = false, onChange, label, hint }) { return <label className={`flex items-start gap-3 rounded-md border p-3 ${disabled ? 'opacity-60' : 'cursor-pointer'}`}><input type="checkbox" checked={checked} disabled={disabled} onChange={(event) => onChange(event.target.checked)} className="mt-1 h-4 w-4" /><span><span className="block text-sm font-medium">{label}</span>{hint && <span className="mt-1 block text-xs text-muted-foreground">{hint}</span>}</span></label>; }
